'use client';

import { useCallback, useEffect, useState } from 'react';

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

interface UseRecaptchaReturn {
  executeRecaptcha: (action: string) => Promise<string | null>;
  isConfigured: boolean;
  isLoaded: boolean;
  isExecuting: boolean;
}

// reCAPTCHA v3 site key. Sourced ONLY from the environment — never hardcoded.
// (A previous version hardcoded a fallback string that was actually the
// Google Maps API key, not a reCAPTCHA key: a leaked secret AND non-functional.
// SonarQube secrets:S6334. The leaked key has been rotated.)
// `NEXT_PUBLIC_*` is inlined into the client bundle at build time, which is
// expected for a reCAPTCHA *site* key — it's public by design. The secret
// half (the verification secret) lives server-side in RECAPTCHA_SECRET_KEY.
const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? '';

const RECAPTCHA_SCRIPT_ID = 'recaptcha-v3-script';
const RECAPTCHA_BADGE_Z_INDEX = '9999';

// Lazily inject the reCAPTCHA script on first hook use. Previously this lived
// in the global locale layout, costing ~365 KiB on every page even where no
// form exists. Now it only loads on routes that actually call useRecaptcha.
function loadRecaptchaScript(): void {
  if (typeof window === 'undefined') return;
  // No key configured → don't inject a broken script tag. Callers fall back
  // to a null token, which the server-side verify route already tolerates.
  if (!RECAPTCHA_SITE_KEY) {
    console.warn('NEXT_PUBLIC_RECAPTCHA_SITE_KEY is not set — reCAPTCHA disabled.');
    return;
  }
  if (document.getElementById(RECAPTCHA_SCRIPT_ID)) return;
  if (window.grecaptcha) return;

  const script = document.createElement('script');
  script.id = RECAPTCHA_SCRIPT_ID;
  script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
}

/** True when any added node in the mutations is (or contains) the reCAPTCHA badge. */
function mutationsContainBadge(mutations: MutationRecord[]): boolean {
  for (const mutation of mutations) {
    const added = Array.from(mutation.addedNodes);
    for (const node of added) {
      if (!(node instanceof HTMLElement)) continue;
      if (node.classList.contains('grecaptcha-badge') || node.querySelector('.grecaptcha-badge')) {
        return true;
      }
    }
  }
  return false;
}

/** Keep the badge on `document.body` so section filters/transforms cannot trap it. */
function elevateRecaptchaBadge(): void {
  if (typeof document === 'undefined') return;

  const badge = document.querySelector<HTMLElement>('.grecaptcha-badge');
  if (!badge) return;

  if (badge.parentElement !== document.body) {
    document.body.appendChild(badge);
  }

  badge.style.setProperty('position', 'fixed', 'important');
  badge.style.setProperty('z-index', RECAPTCHA_BADGE_Z_INDEX, 'important');
}

export const useRecaptcha = (): UseRecaptchaReturn => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    loadRecaptchaScript();
    if (!RECAPTCHA_SITE_KEY) return;

    let cancelled = false;
    const waitForReady = () => {
      if (cancelled) return;
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          if (cancelled) return;
          elevateRecaptchaBadge();
          setIsLoaded(true);
        });
      } else {
        window.setTimeout(waitForReady, 100);
      }
    };
    waitForReady();

    // Badge can appear after the first execute — promote it when it lands in the DOM.
    const observer = new MutationObserver((mutations) => {
      if (cancelled) return;
      if (mutationsContainBadge(mutations)) {
        elevateRecaptchaBadge();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, []);

  const executeRecaptcha = useCallback(
    async (action: string): Promise<string | null> => {
      if (!RECAPTCHA_SITE_KEY) return null; // not configured — skip silently
      if (!isLoaded || !window.grecaptcha) {
        console.warn('reCAPTCHA not loaded yet');
        return null;
      }

      try {
        setIsExecuting(true);
        const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
        elevateRecaptchaBadge();
        return token;
      } catch (error) {
        console.error('❌ reCAPTCHA execution failed:', error);
        return null;
      } finally {
        setIsExecuting(false);
      }
    },
    [isLoaded],
  );

  return {
    executeRecaptcha,
    isConfigured: Boolean(RECAPTCHA_SITE_KEY),
    isLoaded,
    isExecuting,
  };
};

// Helper function to verify token on the server side
export const verifyRecaptchaToken = async (token: string, action?: string): Promise<boolean> => {
  try {
    const response = await fetch('/api/recaptcha/verify', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, action }),
    });

    const data = await response.json();
    return data.success && data.score > 0.5; // You can adjust the score threshold
  } catch (error) {
    console.error('reCAPTCHA verification failed:', error);
    return false;
  }
};
